import { getCategoryDescription, getCategoryLabel, getCategoryIcon } from '../config/categoryConfig'
import {
  BrowseCategoriesResponse,
  CategorySection as ApiCategorySection,
  BrowseCategoryItem,
  BrowseCategorySection,
  BrowseTabData,
  FundNature,
  BrowseTabId
} from '../types/peer-analyser'

/**
 * Build CategoryItem from category enum ID
 */
function buildCategoryItem(categoryId: string): BrowseCategoryItem {
  return {
    id: categoryId,
    label: getCategoryLabel(categoryId),
    description: getCategoryDescription(categoryId),
    Icon: getCategoryIcon(categoryId)
  }
}

/**
 * Build CategorySection from API section
 * @param apiSection - The API section data
 * @param sectionIdPrefix - Prefix for the section ID (FundNature enum value)
 * @param index - Index of the section in the list (to ensure uniqueness when titles repeat)
 */
function buildCategorySection(
  apiSection: ApiCategorySection,
  sectionIdPrefix: FundNature,
  index: number
): BrowseCategorySection | null {
  if (!apiSection?.categoryTitle || !apiSection?.categoryType?.length) {
    return null
  }

  // Generate a unique ID for the section based on title and index
  // This ensures uniqueness even when multiple sections have the same title
  const sectionId = `${sectionIdPrefix}_${apiSection.categoryTitle.toUpperCase().replace(/\s+/g, '_')}_${index}`

  return {
    id: sectionId,
    title: apiSection.categoryTitle,
    categories: apiSection.categoryType.filter(Boolean).map(buildCategoryItem)
  }
}

/**
 * Build browse categories data structure from API response
 */
export function buildBrowseCategoriesData(apiResponse: BrowseCategoriesResponse): BrowseTabData[] {
  if (!apiResponse) {
    return []
  }

  const tabs: BrowseTabData[] = []

  // Build Equity Funds tab
  if (apiResponse?.equityFundsList?.length > 0) {
    const sections =
      apiResponse?.equityFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.EQUITY, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.EQUITY_FUNDS,
        label: 'Equity Funds',
        sections
      })
    }
  }

  // Build Debt Funds tab
  if ((apiResponse?.debtFundsList ?? []).length > 0) {
    const sections =
      apiResponse?.debtFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.DEBT, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.DEBT_FUNDS,
        label: 'Debt Funds',
        sections
      })
    }
  }

  // Build Index Funds tab
  if (apiResponse?.indexFundsList?.length > 0) {
    const sections =
      apiResponse?.indexFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.INDEX, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.INDEX_FUNDS,
        label: 'Index Funds',
        sections
      })
    }
  }

  // Build Hybrid Funds tab
  if (apiResponse?.hybridFundsList?.length > 0) {
    const sections =
      apiResponse?.hybridFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.HYBRID, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.HYBRID_FUNDS,
        label: 'Hybrid Funds',
        sections
      })
    }
  }

  // Build Commodity Funds tab
  if (apiResponse?.commodityFundsList?.length > 0) {
    const sections =
      apiResponse?.commodityFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.COMMODITY, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.COMMODITY_FUNDS,
        label: 'Gold & Silver Funds',
        sections
      })
    }
  }

  // Build Solution Oriented Funds tab
  if (apiResponse?.solutionOrientedFundsList?.length > 0) {
    const sections =
      apiResponse?.solutionOrientedFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.SOLUTIONS_ORIENTED, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.SOLUTIONS_ORIENTED_FUNDS,
        label: 'Solution Oriented Funds',
        sections
      })
    }
  }

  // Build Global Funds tab
  if (apiResponse?.globalFundsList?.length > 0) {
    const sections =
      apiResponse?.globalFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.GLOBAL, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.GLOBAL_FUNDS,
        label: 'Global Funds',
        sections
      })
    }
  }

  // Build Fund of Funds tab
  if ((apiResponse?.fofFundsList ?? []).length > 0) {
    const sections =
      apiResponse?.fofFundsList
        ?.map((section, index) => buildCategorySection(section, FundNature.FUND_OF_FUNDS, index))
        ?.filter((section): section is BrowseCategorySection => section !== null) || []

    if (sections.length > 0) {
      tabs.push({
        id: BrowseTabId.FUND_OF_FUNDS,
        label: 'Fund of Funds',
        sections
      })
    }
  }
  return tabs
}

/**
 * Extract categories from a specific tab by fund nature
 * Used for SharedLayoutMenu tabs - filters by FundNature enum
 */
export function extractCategoriesByFundNature(tabs: BrowseTabData[], fundNature: FundNature): BrowseCategoryItem[] {
  const tabIdMap: Partial<Record<BrowseTabId, FundNature>> = {
    [BrowseTabId.EQUITY_FUNDS]: FundNature.EQUITY,
    [BrowseTabId.DEBT_FUNDS]: FundNature.DEBT,
    [BrowseTabId.INDEX_FUNDS]: FundNature.INDEX,
    [BrowseTabId.HYBRID_FUNDS]: FundNature.HYBRID,
    [BrowseTabId.COMMODITY_FUNDS]: FundNature.COMMODITY,
    [BrowseTabId.SOLUTIONS_ORIENTED_FUNDS]: FundNature.SOLUTIONS_ORIENTED,
    [BrowseTabId.GLOBAL_FUNDS]: FundNature.GLOBAL,
    [BrowseTabId.FUND_OF_FUNDS]: FundNature.FUND_OF_FUNDS
  }

  const categories: BrowseCategoryItem[] = []

  tabs?.forEach((tab) => {
    if (!tab?.id) return

    if (tabIdMap[tab.id as BrowseTabId] === fundNature) {
      tab?.sections?.forEach((section) => {
        section?.categories?.forEach((category) => {
          // Avoid duplicates by checking if category already exists
          if (category?.id && !categories.find((c) => c?.id === category?.id)) {
            categories.push(category)
          }
        })
      })
    }
  })

  return categories
}

/**
 * Extract all categories from all tabs into a flat list
 * Used for SharedLayoutMenu tabs (deprecated - use extractCategoriesByFundNature instead)
 */
export function extractAllCategories(tabs: BrowseTabData[]): BrowseCategoryItem[] {
  const allCategories: BrowseCategoryItem[] = []

  tabs?.forEach((tab) => {
    tab?.sections?.forEach((section) => {
      section?.categories?.forEach((category) => {
        // Avoid duplicates by checking if category already exists
        if (category?.id && !allCategories.find((c) => c?.id === category?.id)) {
          allCategories.push(category)
        }
      })
    })
  })

  return allCategories
}
